"use client";

import React, { useEffect, useRef, useState } from "react";
import useModalScrollLock from "@/hooks/useModalScrollLock";
import PopupForm from "@/components/newPageComponent/CommonSection/PopupForm";
import { PAGE_GUTTER } from "./pageWidth";

interface HomeProps {
  pageName: string;
}
const VIDEO_BASE =
  "https://ik.imagekit.io/mileseducation/miles_website/home_2026/banner_bg.mp4";
const VIDEO_SRC_DESKTOP = VIDEO_BASE;
const VIDEO_SRC_MOBILE = `${VIDEO_BASE}?tr=w-800`;

const POSTER_BASE =
  "https://ik.imagekit.io/mileseducation/miles_website/home_2026/poster_img.webp";
const POSTER_SRC = `${POSTER_BASE}?tr=f-webp,q-65,w-1400`;
const POSTER_SRCSET = [640, 800, 1080, 1400, 1600, 1920, 2400]
  .map((width) => `${POSTER_BASE}?tr=f-webp,q-65,w-${width} ${width}w`)
  .join(", ");

/* The desktop poster is a wide (~2.6:1) layout with black padding on the
   left, while the video is 16:9, so under object-cover they crop and scale
   differently and the picture jumps when the video fades in. On mobile, use
   the video's own first frame (ImageKit thumbnail) so the swap is seamless. */
const POSTER_MOBILE_BASE = `${VIDEO_BASE}/ik-thumbnail.jpg`;
const POSTER_MOBILE_SRCSET = [480, 640, 800, 1080]
  .map(
    (width) =>
      `${POSTER_MOBILE_BASE}?tr=so-0,f-webp,q-65,w-${width} ${width}w`,
  )
  .join(", ");

const HomeBannerNew: React.FC<HomeProps> = ({ pageName }) => {
  const [isFormModalOpen, setIsFormModalOpen] = useState<boolean>(false);

  const sectionRef = useRef<HTMLElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoSrc, setVideoSrc] = useState<string | null>(null);
  const [videoReady, setVideoReady] = useState<boolean>(false);

  const handleFormModelOpen = () => {
    sessionStorage.setItem("modal-open", "true");
    setIsFormModalOpen(true);
  };

  const handleFormModelClose = () => {
    sessionStorage.removeItem("modal-open");
    setIsFormModalOpen(false);
  };

  useModalScrollLock(isFormModalOpen, {
    restoreScrollPosition: true,
    preventTouchScroll: true,
  });

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const src = window.matchMedia("(min-width: 768px)").matches
      ? VIDEO_SRC_DESKTOP
      : VIDEO_SRC_MOBILE;

    let cancelled = false;

    const activate = () => {
      if (cancelled) return;
      if (document.readyState === "complete") {
        setVideoSrc(src);
      } else {
        window.addEventListener(
          "load",
          () => {
            if (!cancelled) setVideoSrc(src);
          },
          { once: true },
        );
      }
    };

    const el = sectionRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      activate();
      return () => {
        cancelled = true;
      };
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          activate();
          observer.disconnect();
        }
      },
      { rootMargin: "200px" },
    );

    observer.observe(el);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!videoSrc) return;
    const video = videoRef.current;
    if (!video) return;
    video.load();
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === "function") {
      playPromise.catch(() => {
        /* autoplay blocked — poster stays visible, no crash */
      });
    }
  }, [videoSrc]);

  return (
    <section
      ref={sectionRef}
      /* min-h, not h: the copy column is the tallest thing in here, and at
         tablet widths (the 46% column is only ~350px wide there) it can run
         past the designed height. A fixed height would clip it against
         overflow-hidden; a min-height keeps the designed size everywhere it
         fits and lets the banner grow the few pixels it needs where it
         doesn't. The media below is absolutely positioned, so it follows. */
      className="relative w-full overflow-hidden bg-black md:min-h-[470px] xl:min-h-[585px] min-[1486px]:min-h-[39.4vw]"
      style={{ contain: "layout" }}
    >
      {/* Content — stacked on top on mobile, left side over black on desktop */}
      <div className={`relative z-10 w-full md:min-h-[inherit] ${PAGE_GUTTER}`}>
        {/* pt-[50px] rather than the old `relative top-[25px]`: with
            justify-center a top pad of 2× shifts the block down by ×, so the
            optical nudge is the same 25px, but this one is part of the layout
            and so can't be clipped off the bottom. */}
        <div className="flex max-w-md flex-col justify-center pb-0 pt-24 text-left md:min-h-[inherit] md:max-w-[47%] md:pb-0 md:pt-[50px] min-[1700px]:pl-[50px]">
          <p className="mt-5 mb-5 text-[18px] font-normal leading-snug text-white md:mt-7 md:text-[26px]">
            {/* Become an{" "} */}
            <span className="text-[#FFBA08] font-bold rounded-lg border border-[#FFBA08] px-3 py-[6px]">
              <span className="text-white">Be a </span>Certified AI-Ready
            </span>
          </p>
          {/* <p className="text-base md:text-lg font-medium leading-normal text-white tracking-[-0.48px]">
            Become an
          </p> */}
          <h1 className="text-[34px] font-bold leading-[37px] md:leading-[50px] text-[#FFBA08] md:text-[48px] lg:text-[52px] mb-2">
            <span className="text-white">
              Global Accounting &
              <br />
              Finance Professional
            </span>
          </h1>
          <p className="mt-3 w-fit text-[16px] font-semibold leading-tight md:mt-7 md:text-[24px] text-white">
            <a
              className="text-[#FFBA08] hover:text-white border-r border-gray-500 py-0 pr-5 "
              href="/cpa"
            >
              U.S. CPA{" "}
            </a>{" "}
            <a
              className="text-[#FFBA08] hover:text-white border-r border-gray-500 py-0 px-5 "
              href="/cma"
            >
              U.S. CMA
            </a>{" "}
            <a
              className="text-[#FFBA08] hover:text-white border-gray-500 py-0 px-5 "
              href="/ea"
            >
              U.S. EA
            </a>
          </p>

          <p className="mt-2 text-[12px] md:text-base font-normal text-white md:mt-3">
            Integrated with{" "}
            <span className="text-[#FFBA08]">Miles AI Labs </span> 
            - world’s 1st AI Labs for<br/> Accountants.{" "}
            <span className="text-[#FFBA08]">
              Build AI Agents & Accounting workflows
            </span>{" "}
            in<br/> Co-pilot Studio & Power Automate for Audit, Tax, CAS & more
          </p>

          <div className="mt-5 md:mt-7">
            <button
              onClick={handleFormModelOpen}
              className="gcc-border w-fit text-sm md:text-base font-bold text-white"
              style={{
                borderRadius: "20px 20px 20px 0",
                background:
                  "linear-gradient(96deg, rgba(51, 153, 255, 0.00) -35.61%, rgba(0, 108, 185, 0.60) 47.86%, rgba(51, 153, 255, 0.00) 176.03%)",
                padding: "10px 60px",
              }}
            >
              Enquire Now
            </button>
          </div>
        </div>
      </div>

      <div className="relative -mt-12 h-[240px] w-full sm:h-[440px] md:absolute md:right-0 md:top-0 md:z-0 md:mt-0 md:h-full md:w-[70%]">
        <picture>
          <source
            media="(max-width: 767px)"
            srcSet={POSTER_MOBILE_SRCSET}
            sizes="100vw"
          />
          <img
            src={POSTER_SRC}
            srcSet={POSTER_SRCSET}
            sizes="70vw"
            alt=""
            aria-hidden="true"
            width={1400}
            height={788}
            fetchPriority="high"
            decoding="async"
            className="h-full w-full object-cover object-[95%_center] md:absolute md:inset-0 md:z-0 md:object-right"
          />
        </picture>
        <video
          ref={videoRef}
          onPlaying={() => setVideoReady(true)}
          className={`absolute inset-0 z-[1] h-full w-full object-cover object-[95%_center] transition-opacity duration-1000 ease-out md:object-right motion-reduce:transition-none ${
            videoReady ? "opacity-100" : "opacity-0"
          }`}
          muted
          loop
          autoPlay
          playsInline
          {...{ "webkit-playsinline": "true" }}
          preload="none"
          aria-hidden="true"
          tabIndex={-1}
        >
          {videoSrc ? <source src={videoSrc} type="video/mp4" /> : null}
        </video>
        {/* Mobile-only top fade so the video blends into the black bg above it */}
        <div
          className="pointer-events-none absolute inset-x-0 top-[-1px] z-[2] h-40 md:hidden"
          style={{
            background:
              "linear-gradient(180deg, #000 0%, rgba(0,0,0,0.7) 10%, rgba(0,0,0,0) 100%)",
          }}
          aria-hidden="true"
        />
      </div>

      {isFormModalOpen && (
        <PopupForm pageName={pageName} handleClose={handleFormModelClose} />
      )}
    </section>
  );
};

export default HomeBannerNew;
